import React, {useState} from 'react';
import {
    PublicClientApplication,
    IPublicClientApplication,
    LogLevel,
    InteractionStatus, EventType, Configuration,
} from '@azure/msal-browser';
import {
    AuthenticatedTemplate,
    MsalProvider,
    UnauthenticatedTemplate,
    useIsAuthenticated,
    useMsal
} from '@azure/msal-react';
import {ReactNode, useEffect} from 'react';
import {Redirect} from 'react-router-dom';
import {LoadingSpinner} from "../../../common";
import {OAuthManager} from "./oauth";
import {OAuthManagerContext} from './context';
import { ApplicationRootUrl } from 'common/component/ApplicationRootContext';

export const AZURE_LOGIN_SCOPES = ['openid', 'profile', 'email', 'offline_access'];


const msalCPConfig: Configuration = {
    auth: {
        clientId: process.env.REACT_APP_AZURE_CLIENT_PLACEMENT_INTERNAL_ID!,
        authority: process.env.REACT_APP_AZURE_INTERNAL_AUTHORITY!,
        knownAuthorities: process.env.REACT_APP_AZURE_INTERNAL_KNOW_AUTHORITIES!.split(','),
        navigateToLoginRequestUrl: true,
        redirectUri: process.env.REACT_APP_AZURE_INTERNAL_CP_REDIRECT_URI,
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true,
    },
    system: {
        loggerOptions: {
            logLevel: LogLevel.Verbose,
        },
    },
}

const msalUMConfig: Configuration = {
    auth: {
        clientId: process.env.REACT_APP_AZURE_CLIENT_UM_ID!,
        authority: process.env.REACT_APP_AZURE_INTERNAL_AUTHORITY!,
        knownAuthorities: process.env.REACT_APP_AZURE_INTERNAL_KNOW_AUTHORITIES!.split(','),
        navigateToLoginRequestUrl: true,
        redirectUri: process.env.REACT_APP_AZURE_INTERNAL_UM_REDIRECT_URI,
    },
    cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: true,
    },
    system: {
        loggerOptions: {
            logLevel: LogLevel.Verbose,
        },
    },
}

const msalCPInstance = new PublicClientApplication(msalCPConfig);

// Default to using the first account if no account is active on page load
if (!msalCPInstance.getActiveAccount() && msalCPInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalCPInstance.setActiveAccount(msalCPInstance.getAllAccounts()[0]);
}

msalCPInstance.addEventCallback((event) => {
    const {eventType, payload} = event;
    const success = eventType === EventType.LOGIN_SUCCESS ||
        eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        eventType === EventType.SSO_SILENT_SUCCESS;
    if (success && payload && 'account' in payload && payload.account) {
        msalCPInstance.setActiveAccount(payload.account);
    }
});

const msalUMInstance = new PublicClientApplication(msalUMConfig);

// Default to using the first account if no account is active on page load
if (!msalUMInstance.getActiveAccount() && msalUMInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalUMInstance.setActiveAccount(msalUMInstance.getAllAccounts()[0]);
}

msalUMInstance.addEventCallback((event) => {
    const {eventType, payload} = event;
    const success = eventType === EventType.LOGIN_SUCCESS ||
        eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        eventType === EventType.SSO_SILENT_SUCCESS;
    if (success && payload && 'account' in payload && payload.account) {
        msalUMInstance.setActiveAccount(payload.account);
    }
});

class AzureOAuthManager implements OAuthManager {
    readonly #configName: string;
    readonly #delegate: IPublicClientApplication;
    readonly #scopes: string[];

    constructor(configName: string, delegate: IPublicClientApplication, scopes: string[]) {
        this.#configName = configName;
        this.#delegate = delegate;
        this.#scopes = scopes;
    }

    loginUrl(): string {
        return `/${this.#configName}`;
    }

    async isAuthenticated(): Promise<boolean> {
        return !!this.#delegate.getActiveAccount();
    }

    async jwtForApi(): Promise<string> {
        return this.accessToken();
    }

    async accessToken(): Promise<string> {
        try {
            const response = await this.#delegate.acquireTokenSilent({
                scopes: this.#scopes,
                account: this.#delegate.getActiveAccount() ?? undefined,
            });
            return response.idToken;
        } catch (e) {
            // If silent token acquisition fails, initiate a redirect login
            await this.#delegate.loginRedirect({
                scopes: this.#scopes,
                redirectUri: this.#delegate.getConfiguration().auth.redirectUri,
            });

            throw new Error("Silent token acquisition failed, redirecting to login.");
        }
    }

    async idToken(): Promise<string> {
        try {
            const response = await this.#delegate.acquireTokenSilent({
                scopes: this.#scopes,
                account: this.#delegate.getActiveAccount() ?? undefined,
            });
            return response.idToken;
        } catch (e) {
            // If silent token acquisition fails, initiate a redirect login
            await this.#delegate.loginRedirect({
                scopes: this.#scopes,
                redirectUri: this.#delegate.getConfiguration().auth.redirectUri,
            });

            throw new Error("Silent token acquisition failed, redirecting to login.");
        }
    }


    refreshToken(): string {
        return '';
    }

    async clearToken(): Promise<void> {
    }
}

type LoginPageProps = {
    redirectUri: string,
    configName: string,
}

export const LoginPage = ({redirectUri, configName}: LoginPageProps) => {
    const {instance, inProgress} = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [redirectInitiated, setRedirectInitiated] = useState(false);

    useEffect(() => {
        if (!isAuthenticated && inProgress === InteractionStatus.None && !redirectInitiated) {
            setRedirectInitiated(true);
            void instance.loginRedirect({
                scopes: AZURE_LOGIN_SCOPES,
                redirectUri: redirectUri,
            });
        }
    }, [instance, isAuthenticated, inProgress, redirectUri, redirectInitiated]);

    if (isAuthenticated && inProgress === InteractionStatus.None) {
        return <Redirect to={'/' + configName}/>;
    }
    return <LoadingSpinner text={'Checking your session'} centerScreen={true}/>;
};


type AzureOauth2EnvironmentProps = {
    children: ReactNode;
    applicationType: 'Client Placement' | 'User Management';
}

function getClientApplication(clientType: 'Client Placement' | 'User Management') {
    if (clientType === "Client Placement") {
        return msalCPInstance;
    } else {
        return msalUMInstance;
    }

}

export const AzureOauth2Environment = ({children, applicationType}: AzureOauth2EnvironmentProps) => {
    const instance = getClientApplication(applicationType)
    const configName = applicationType === 'Client Placement' ?  ApplicationRootUrl.AZURE_INTERNAL_CP_ROUTE_NAME : ApplicationRootUrl.AZURE_INTERNAL_UM_ROUTE_NAME;

    const [oauthManager] = useState(new AzureOAuthManager(configName, instance, AZURE_LOGIN_SCOPES));
    return (
        <OAuthManagerContext.Provider value={oauthManager}>
            <MsalProvider instance={instance}>
                <AuthenticatedTemplate>
                    {children}
                </AuthenticatedTemplate>
                <UnauthenticatedTemplate>
                    <LoginPage configName={configName}
                               redirectUri={
                                   applicationType === 'Client Placement' ?
                                       process.env.REACT_APP_AZURE_INTERNAL_CP_REDIRECT_URI! :
                                       process.env.REACT_APP_AZURE_INTERNAL_UM_REDIRECT_URI!
                               }/>
                </UnauthenticatedTemplate>
            </MsalProvider>
        </OAuthManagerContext.Provider>
    );
};
